import React from 'react';
import filter from 'lodash/filter';
import styled from 'styled-components';
import moment from 'moment';

import EventsColumn from './components/EventsColumn';
import ExternalLink from 'src/presentation/components/pages/shared/ExternalLink';
import RouterLinkButton from 'src/presentation/components/RouterLinkButton';
import RowActions from 'src/presentation/components/pages/integrations/webhooks/RowActions';
import TablePage from 'src/presentation/components/pages/shared/TablePage';
import Toggle from 'src/presentation/components/forms/Toggle';
import WebhooksEditor from './WebhooksEditor';
import useWebhooks from 'src/presentation/components/pages/integrations/webhooks/hooks/useWebhooks';
import webhooksResource from 'src/actions/integrations/webhooksResource';
import { EmptyStateWrapper } from 'src/presentation/components/pages/shared/TableList';
import { Row } from 'src/presentation/components/forms/Layout';
import { isLoadingStatus } from 'src/presentation/hooks/useResource';
import { Webhook } from 'src/actions/integrations/webhook';
import ExclamationCircleIcon from '@ad-scripts/presentation/components/lib/icons/exclamation_circle_icon';
import { Tooltip } from 'src/presentation/components/agent_desktop_components';
import { OrderDirection } from 'src/presentation/components/pages/shared/utils/sortingUtils';

const DevDocumentationUrl = 'https://developer.gladly.com/rest/#tag/Webhooks';

export const filterFunc = (items: Webhook[], searchString: string): Webhook[] => {
  if (!searchString) {
    return items;
  }

  const nameMatch = (item: Webhook) => item.name.toLowerCase().includes(searchString.trim().toLowerCase()) as boolean;
  const urlMatch = (item: Webhook) =>
    (item.url ?? '').toLowerCase().includes(searchString.trim().toLowerCase()) as boolean;

  return filter(items, item => nameMatch(item) || urlMatch(item));
};

const Webhooks = ({ root }): JSX.Element => {
  const { webhooks, loadingState, errors, toggleStatus } = useWebhooks();

  const columnConfig = getColumnConfig(root, toggleStatus);
  const sortingConfig = getInitialSorting();
  const description = getDescription();
  const emptyState = getEmptyState(root);
  return (
    <TablePage
      actions={createWebhookAction(root, { dataAid: 'webhooks' })}
      columnConfig={columnConfig}
      data-aid="admin-webhooks-page"
      description={description}
      editor={WebhooksEditor}
      emptyStateRender={emptyState}
      errors={errors}
      filterFunc={filterFunc}
      initialSorting={sortingConfig}
      isLoading={isLoadingStatus(loadingState)}
      items={webhooks}
      name="Webhooks"
      renderRowActions={props => <RowActions {...props} />}
      resetErrors={webhooksResource.actions.resetErrors}
      root={root}
    />
  );
};

function getColumnConfig(root, toggleStatus) {
  return [
    {
      attr: 'disabledAtDueToRetryLimit',
      customRenderer: item => {
        if (item.enabled || !item.disabledAtDueToRetryLimit) {
          return null;
        }
        const date = moment.utc(item.disabledAtDueToRetryLimit);
        const dateString = moment(date).format('DD, YYYY hh:mm A');

        return (
          <StyledTooltip
            message={`This webhook was deactivated on ${moment.months(date.month())} ${dateString} ${date.zoneAbbr()}`}
          >
            <StyledExclamationCircleIcon />
          </StyledTooltip>
        );
      },
      customColumnStyle: () => ({ width: '24px' }),
    },
    {
      attr: 'enabled',
      label: 'Status',
      customRenderer: item => (
        <Toggle
          data-aid={`status-source-${item.id}`}
          value={item.enabled}
          debounceInterval={300}
          onChange={() => {
            toggleStatus(item);
          }}
        />
      ),
      customColumnStyle: () => ({ width: '64px' }),
    },
    { attr: 'name', label: 'Name', customColumnStyle: () => ({ width: '240px' }) },
    { attr: 'url', label: 'URL', customColumnStyle: () => ({ width: '500px' }) },
    { attr: 'events', label: 'Events', customRenderer: item => <EventsColumn item={item}></EventsColumn> },
  ];
}

function getInitialSorting() {
  const attrsToDirections = new Map();
  attrsToDirections.set('disabledAtDueToRetryLimit', OrderDirection.asc);
  attrsToDirections.set('enabled', OrderDirection.desc);
  return { attrsToDirections: attrsToDirections };
}

function getDescription() {
  return (
    <div>
      Configure webhooks to get notified when certain events (e.g. when a conversation is created) occur in Gladly as a
      POST request to an endpoint
      <br /> of your choice. See our{' '}
      <ExternalLink data-aid="developer-help" to={DevDocumentationUrl} label="Developer Documentation" /> for more info.
    </div>
  );
}

function getEmptyState(root) {
  return (
    <StyledEmptyStateWrapper data-aid="webhooks-emptyState">
      <Row>
        <EmptyStateText data-aid="emptyStateText">
          No Webhooks have been created. Check out our{' '}
          <ExternalLink data-aid="emptyState-developer-help" to={DevDocumentationUrl} label="Developer Documentation" />
          .
        </EmptyStateText>
      </Row>
      <Row data-aid="emptyStateButton">{createWebhookAction(root, { dataAid: 'webhooks-emptyState' })}</Row>
    </StyledEmptyStateWrapper>
  );
}

const createWebhookAction = (root, { dataAid }) => (
  <RouterLinkButton data-aid={`${dataAid}-createButton`} to={`${root}/new`}>
    Create Webhook
  </RouterLinkButton>
);

const StyledEmptyStateWrapper = styled(EmptyStateWrapper)`
  height: 50vh;
`;

const EmptyStateText = styled.span`
  text-align: center;
`;

const StyledTooltip = styled(Tooltip)`
  margin-left: -4px;
  position: absolute;
`;

const StyledExclamationCircleIcon = styled(ExclamationCircleIcon)`
  fill: ${p => p.theme.colors.red400};
  width: 24px;
  padding: 4px;
  display: flex;
  margin-top: -12px;
`;

export default Webhooks;
